// @flow

import styled from '@emotion/styled'
import type { GatsbyLinkProps } from 'gatsby'
import * as React from 'react'

import { colors, lineHeights, media } from '@toggl/style'

import { Logo } from '../logos/Logo'
import * as text from '../text'

type JobType = {
  title: string,
  position: string,
  description: string,
  team: 'track' | 'hire' | 'plan',
  slug: string,
  ...
}

type Props = {
  title: string,
  jobs: $ReadOnlyArray<JobType>,
  Link: React.ComponentType<GatsbyLinkProps<any>>,
}
export const ThreeCol = ({ title, jobs, Link }: Props) => {
  const count = jobs.length
  const summaryText = `${count} position${count === 1 ? '' : 's'}`

  return (
    <Root>
      <HeaderWrapper>
        <Summary>{summaryText}</Summary>
        <Title>{title}</Title>
      </HeaderWrapper>

      {jobs.map((vacancy, id) => (
        <Link to={`/jobs/${vacancy.slug}/`} key={id}>
          <VacancyWrapper>
            <VacancyTitleWrapper>
              <VacancyTitle>{vacancy.title}</VacancyTitle>
              {vacancy.position}
            </VacancyTitleWrapper>

            <VacancyDescription>{vacancy.description}</VacancyDescription>

            <TeamStyled>
              <div>
                <Logo kind={vacancy.team} />
              </div>
            </TeamStyled>
          </VacancyWrapper>
        </Link>
      ))}
    </Root>
  )
}

const TeamStyled: React.ComponentType<any> = styled.div`
  margin-top: 33px;

  svg {
    height: 28px;
  }

  ${media.mq[1]} {
    margin-top: 0;
    flex: 0 0 140px;
  }

  ${media.mq[2]} {
    flex: 0 0 200px;
  }
`

const VacancyDescription: React.ComponentType<any> = styled.div`
  display: none;

  ${media.mq[1]} {
    display: block;
    flex: 2;
    margin: 0 25px;
  }
  ${media.mq[2]} {
    margin: 0 50px;
  }
`

const VacancyTitleWrapper: React.ComponentType<any> = styled.div`
  ${media.mq[1]} {
    flex: 1;
  }
`

const VacancyTitle: React.ComponentType<any> = styled.div`
  ${text.paragraph1};
  margin-bottom: 7px;

  ${media.mq[1]} {
    ${text.paragraph1};
  }
`

const VacancyWrapper: React.ComponentType<any> = styled.div`
  ${text.paragraph4}
  display: flex;
  flex-direction: column;
  padding: 30px;
  margin: 10px 0;
  background-color: ${colors.darkPurple};
  color: ${colors.beige};

  ${media.mq[1]} {
    margin: 15px 0;
    flex-direction: row;
    justify-content: space-between;
    &:hover {
      background-color: ${colors.fadedPurpleAlt};
    }
  }
  ${media.mq[2]} {
    padding: 45px 55px;
  }
`

const HeaderWrapper: React.ComponentType<any> = styled.div`
  ${text.heading3};
  margin: 0 0 25px;

  ${media.mq[2]} {
    margin-bottom: 45px;
  }
`

const Title: React.ComponentType<any> = styled.div`
  ${text.heading3};
  color: ${colors.yellow};
  line-height: ${lineHeights.heading1};

  ${media.mq[1]} {
    display: flex;
  }
`
const Summary: React.ComponentType<any> = styled.div`
  ${text.heading5}
  color: ${colors.beige};
  margin-bottom: 6px;

  ${media.mq[1]} {
    ${text.paragraph1};
    margin-bottom: 0;
    display: flex;
  }
`

const Root: React.ComponentType<any> = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto 20px;
  ${media.mq[1]} {
    margin-bottom: 40px;
  }
`
